import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { ShipmentDateFilterType } from '@shared-features/enums/shipment-date-filter-type.enum';
import { DateRangeType } from '@shared/enums/date-range-type.enum';
import { SortOptions } from '@shared/enums/sort-options.enum';
import { TabModel } from '../../model/tab.model';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FunctionUtil } from '../../utils/function.util';
import { EnumToArrayPipe } from '@shared/pipes/enum-to-array.pipe';
import { TrackingType } from '@shared/enums/tracking-type.enum';
import { ShipmentRequestsMultiplicationType } from '@shared/enums/shipment-requests-multiplication-type.enum';
import { ColumnOptionsListPage } from '@shared/enums/column-options-list-page.enum';
import { CustomerType } from '@shared/enums/customer-type.enum';
import { BaseComponent } from '../base-component/base.component';
import { dateRangePreview } from '@shared-features/utils/date-range-preview.util';
import debounce from 'lodash/debounce';
import { ColumnOptionsService } from '@shared/services/column-options.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { RolesConstants } from '@shared/model/roles-constants';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-list-filter-controls',
  templateUrl: './list-filter-controls.component.html',
  styleUrls: ['./list-filter-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFilterControlsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() includeSearchControl = true;
  @Input() includeShipmentFilter = false;
  @Input() includeDateControl = false;
  @Input() includePickupDateLabel = false;
  @Input() includePickupDateControl = false;
  @Input() includeSortByControl = true;
  @Input() includeFilterButton = true;
  @Input() includeShipmentSpecificFleetFilter = false;
  @Input() breakControlsIntoMultipleLines = false;
  @Input() includeLastRefreshTime = false;
  @Input() isB2c = false;
  @Input() includeExportMenuButton = false;
  @Input() includeExportButton = false;

  @Input() filterNavigationConfig: TabModel;
  @Input() showCustomDateOption = false;
  @Input() searchPlaceholder: string;
  @Input() selectedDateRange: DateRangeType;
  @Input() customerServiceType: CustomerType;
  @Input() shipmentRequestFilterCount: ShipmentRequestsMultiplicationType;
  @Input() trackingType: TrackingType;
  @Input() isBayanVerified: boolean;
  @Input() ExportButtonRole = RolesConstants.SHIPMENT_EXPORT;
  @Input() searchTerm: string;
  @Input() clearAllValues;

  @Input() lastRefreshTime: Date = new Date();
  @Input() showSearchActions = true;
  @Input() PickupDateLabel: string;
  @Input() ShipmentDateFilterType: any = ShipmentDateFilterType;
  @Input() searchKeyEnum;
  @Input() isMultiple = false;
  @Input() dateRangeValue: DateRangeType = DateRangeType.TODAY;
  @Output() sortBy = new EventEmitter();
  @Output() searchValue = new EventEmitter();
  @Output() selectedDateRangeChanged = new EventEmitter();
  @Output() startFrom = new EventEmitter();
  @Output() endAt = new EventEmitter();
  @Output() selectedShipmentDateFilterTypeChanged = new EventEmitter();
  @Output() autoUpdateListToggled = new EventEmitter();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() exportPDF = new EventEmitter<void>();
  @Output() handleExport = new EventEmitter<void>();
  // enum
  public sortOptions = SortOptions;
  public dateRangeType = DateRangeType;
  previewSelectedDate: string;

  form: UntypedFormGroup = this.formBuilder.group({
    from: new UntypedFormControl(null),
    to: new UntypedFormControl(null),
  });

  @Input() maxDate: Date = new Date();
  @Input() shipmentDateFilterTypeValue: ShipmentDateFilterType = this.getDateTypeFilterValue();
  protected readonly RolesConstants = RolesConstants;
  position: ConnectedPosition = {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  };

  @Input() includeLastRefreshTimeShipmentList = false;
  // at most cases it should be enum of key in entity = key in translation
  @Input() columns: { [key: string]: string };
  // it must list of keys in entity provided in columnOptionKeys
  @Input() defaultVisibleColumns: string[];
  @Input() pageName: ColumnOptionsListPage;

  @Output() columnOptionsChanges = new EventEmitter();
  @Output() exportedColumnsFilter = new EventEmitter();
  shipmentListExportFilter = [];
  constructor(
    private formBuilder: UntypedFormBuilder,
    private changeDetector: ChangeDetectorRef,
    private columnOptionsService: ColumnOptionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.columnOptionsService.state$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(({ state, keys }) => {
        this.shipmentListExportFilter = (state || []).map(({ key }) => key);
        this.exportedColumnsFilter.emit(this.shipmentListExportFilter);
        this.changeDetector.markForCheck();
        this.columnOptionsChanges.emit();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { selectedDateRange, shipmentDateFilterTypeValue } = changes;

    if (selectedDateRange && selectedDateRange.currentValue !== selectedDateRange.previousValue) {
      this.dateRangeValue = this.selectedDateRange;
    }

    if (
      shipmentDateFilterTypeValue &&
      shipmentDateFilterTypeValue.currentValue !== shipmentDateFilterTypeValue.previousValue
    ) {
      this.form.patchValue({ from: null, to: null });
    }
    debounce(() => this.changeDetector.markForCheck(), 500);
  }

  onSearch(value: string): void {
    this.searchValue.emit(value);
    // this.selectedDateRangeChanged.emit(DateRangeType.ALL_TIME);
    this.checkPreviewSelectedDate();
  }

  onSelectSortBy(value: string): void {
    this.sortBy.emit(value);
  }

  customDateChanged(): void {
    if (this.form.controls.to.value) {
      this.startFrom.emit(FunctionUtil.convertDate(this.form.controls.from.value));
      this.endAt.emit(FunctionUtil.convertDate(this.form.controls.to.value));
      this.selectedDateRangeChanged.emit(this.dateRangeValue);
    }
  }

  OnDateSelectionChange(item: MatSelectChange): void {
    this.dateRangeValue = item.value;
    if (this.dateRangeValue !== DateRangeType.CUSTOM) {
      this.selectedDateRangeChanged.emit(this.dateRangeValue);
    } else {
      this.form.controls.from.setValue(null);
      this.form.controls.to.setValue(null);
    }
    this.checkPreviewSelectedDate();
    this.changeDetector.markForCheck();
  }

  private getDateTypeFilterValue(): ShipmentDateFilterType {
    const types = new EnumToArrayPipe().transform(this.ShipmentDateFilterType);
    return types[1] ? ShipmentDateFilterType.pickUpDate : ShipmentDateFilterType.createdAt;
  }

  checkPreviewSelectedDate(): void {
    this.previewSelectedDate = dateRangePreview(this.dateRangeValue);
  }

  toggleColumnOptionsMenu(): void {
    this.columnOptionsService.toggleDrawer(true);
  }

  handleExportCsv(): void {
    this.exportCsv.emit();
  }

  onExportPDF(): void {
    this.exportPDF.emit();
  }

  onHandleExport(): void {
    this.handleExport.emit();
  }
  autoUpdateChanges(event: MatSlideToggleChange): void {
    this.autoUpdateListToggled.emit(event.checked);
  }
}
